import React from 'react';
import styled from 'styled-components';
import { Empty, Icon, Label, Text } from 'components';
import { theme, toRem } from 'design';
import { EmployeeType, IEmployee, SiteRole } from 'types';
import { formatJobTitle } from 'utils';
import Sidebar from './components/Sidebar';
import { Content as BaseContent } from './components/styles';

export interface IAddUser extends IEmployee {
  recentlyAddedSiteId?: string;
}

interface IUserAddSidebarProps {
  dataCy?: string;
  users?: IAddUser[];
}

const Content = styled(BaseContent)`
  height: 100%;
`;

const AddedUsersList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  height: 100%;
  list-style: none;
  padding: ${toRem(theme.space.lg)} 0;
  gap: ${toRem(theme.space.md)};
`;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  gap: ${toRem(theme.space.sm)};

  label {
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.regular};
  }
`;

const UserListItemLayout = styled.li`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  min-height: ${toRem(66)};
  margin-bottom: ${toRem(theme.space.md)};
  padding: ${toRem(theme.space.xl)} ${toRem(theme.space.xl)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.white};
  gap: ${toRem(theme.space.sm)};

  svg {
    min-width: ${toRem(24)};
    min-height: ${toRem(24)};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const AddedUserListItem = ({ user }: { user: IAddUser }) => {
  let site = user.sites?.[0];
  if (user.sites?.length > 1) {
    site = (user.sites || []).find((s) => s.id === user.recentlyAddedSiteId) || site;
  }

  // Format the label based on the employee type and site role
  const label = (role: SiteRole) => {
    if (user.type === EmployeeType.ADMIN) return 'Organization Admin';
    if (role === SiteRole.ADMIN) return `Site Admin, ${site?.name}`;
    if (role === SiteRole.MANAGER) return `Manager, ${site?.name}`;
    if (role === SiteRole.EMPLOYEE) return `${formatJobTitle(site?.job?.title)}, ${site?.name}`;
    return null;
  };

  return (
    <UserListItemLayout data-cy="added-user-item">
      <Wrapper>
        <Text size="small" fontWeight="medium">
          {user.name}
        </Text>
        <Label allowWordWrap>{label(site?.role)}</Label>
      </Wrapper>
      <Icon icon="Success" width={24} height={24} fill={theme.color.secondary[400]} />
    </UserListItemLayout>
  );
};

const UserAddSidebar = ({ users = [], dataCy = 'user-add-sidebar' }: IUserAddSidebarProps) => (
  <Sidebar title="Added Users" titleValue={users.length > 0 && `${users.length}`} dataCy={dataCy}>
    <Content>
      {!users.length ? (
        <Empty container icon="People" message="Added users will appear here." />
      ) : (
        <AddedUsersList>
          {users.map((user, index) => (
            <AddedUserListItem key={`user-${user.id}-${index}`} user={user} />
          ))}
        </AddedUsersList>
      )}
    </Content>
  </Sidebar>
);

export default UserAddSidebar;
